import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Calendar,
  Users,
  Building,
  CheckCircle2,
  Send,
  Video,
  MapPin,
  Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import PublicFooter from '../components/PublicFooter';
import SEO, { PageSEO } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function PublicNav() {
  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/public" className="flex items-center gap-2">
            <div className="flex items-center justify-center w-12 h-12 bg-[#1E3A5F] rounded-lg">
              <svg viewBox="0 0 40 40" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4L8 10V18C8 26.84 13.16 34.92 20 37C26.84 34.92 32 26.84 32 18V10L20 4Z" fill="#0EA5E9"/>
                <path d="M16 18L19 21L25 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-[#1E3A5F] leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                DealLab
              </span>
              <span className="text-xs text-sky-600 font-medium -mt-0.5">CE Education</span>
            </div>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/public/courses" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Courses
            </Link>
            <Link to="/public/schedule" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Schedule
            </Link>
            <Link to="/public/instructors" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Instructors
            </Link>
            <Link to="/public/sponsors" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Become a Sponsor
            </Link>
          </div>
          
          <Link to="/public/request">
            <Button className="bg-sky-500 hover:bg-sky-600 text-white">
              Book for Your Team
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
}

export default function PublicRequestPage() {
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    course_id: '',
    preferred_date_1: '',
    preferred_date_2: '',
    preferred_date_3: '',
    estimated_attendees: '',
    delivery_preference: '',
    notes: ''
  });

  useEffect(() => {
    fetchCourses();
    
    // Pre-select course from URL params
    const courseId = searchParams.get('course');
    if (courseId) {
      setFormData(prev => ({ ...prev, course_id: courseId }));
    }
  }, [searchParams]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/courses`);
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.estimated_attendees || parseInt(formData.estimated_attendees) < 1) {
      toast.error('Please enter the estimated number of attendees');
      return;
    }
    
    if (!formData.delivery_preference) {
      toast.error('Please select a delivery preference');
      return;
    }
    
    if (!formData.preferred_date_1) {
      toast.error('Please provide at least one preferred date');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/public/request`, {
        ...formData,
        estimated_attendees: parseInt(formData.estimated_attendees)
      });
      
      setSubmitted(true);
      toast.success('Course request submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company: '',
      course_id: '',
      preferred_date_1: '',
      preferred_date_2: '',
      preferred_date_3: '',
      estimated_attendees: '',
      delivery_preference: '',
      notes: ''
    });
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...PageSEO.request} />
      <PublicNav />
      
      {/* Header */}
      <section className="bg-[#1E3A5F] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Request a Course
          </h1>
          <p className="text-sky-200 text-lg max-w-2xl">
            Bring professional CE education to your brokerage or team. We offer 
            flexible scheduling and customized training options.
          </p>
        </div>
      </section>
      
      {/* Form Section */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {submitted ? (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Request Submitted!
                </h2>
                <p className="text-slate-600 mb-6 max-w-md mx-auto">
                  Thank you for your interest! We've received your course request 
                  and will contact you within 1-2 business days to discuss scheduling 
                  and details.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button 
                    onClick={resetForm}
                    variant="outline" 
                    className="border-[#1E3A5F] text-[#1E3A5F]"
                  >
                    Submit Another Request
                  </Button>
                  <Link to="/public/courses">
                    <Button className="bg-sky-500 hover:bg-sky-600 text-white">
                      Browse Courses
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="pt-8 pb-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Contact Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-sky-500" />
                      Contact Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>First Name *</Label>
                        <Input
                          required
                          value={formData.first_name}
                          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                          data-testid="request-first-name"
                        />
                      </div>
                      <div>
                        <Label>Last Name *</Label>
                        <Input
                          required
                          value={formData.last_name}
                          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                          data-testid="request-last-name"
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          data-testid="request-email"
                        />
                      </div>
                      <div>
                        <Label>Phone *</Label>
                        <Input
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          data-testid="request-phone"
                        />
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <Label>Company/Brokerage *</Label>
                      <Input
                        required
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        placeholder="Your brokerage or company name"
                        data-testid="request-company"
                      />
                    </div>
                  </div>
                  
                  {/* Course Selection */}
                  <div className="border-t border-slate-200 pt-6">
                    <h3 className="text-lg font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
                      <Building className="w-5 h-5 text-sky-500" />
                      Course Details
                    </h3>
                    
                    <div>
                      <Label>Select Course (Optional)</Label>
                      <Select
                        value={formData.course_id}
                        onValueChange={(v) => setFormData({ ...formData, course_id: v })}
                      >
                        <SelectTrigger data-testid="request-course-select">
                          <SelectValue placeholder="Choose a specific course or leave blank for custom" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="custom">Custom/Multiple Courses</SelectItem>
                          {courses.map(course => (
                            <SelectItem key={course.id} value={course.id}>
                              {course.title} ({course.credit_hours} CE hr{course.credit_hours > 1 ? 's' : ''})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">
                        Leave blank or select "Custom" if you want to discuss options
                      </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label>Estimated Attendees *</Label>
                        <Input
                          type="number"
                          min="1"
                          required
                          value={formData.estimated_attendees}
                          onChange={(e) => setFormData({ ...formData, estimated_attendees: e.target.value })}
                          placeholder="Number of participants"
                          data-testid="request-attendees"
                        />
                      </div>
                      <div>
                        <Label>Delivery Preference *</Label>
                        <Select
                          value={formData.delivery_preference}
                          onValueChange={(v) => setFormData({ ...formData, delivery_preference: v })}
                        >
                          <SelectTrigger data-testid="request-delivery">
                            <SelectValue placeholder="Select format" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="webinar">
                              <span className="flex items-center gap-2">
                                <Video className="w-4 h-4" /> Webinar
                              </span>
                            </SelectItem>
                            <SelectItem value="in-person">
                              <span className="flex items-center gap-2">
                                <MapPin className="w-4 h-4" /> In-Person
                              </span>
                            </SelectItem>
                            <SelectItem value="either">Either Works</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  
                  {/* Scheduling */}
                  <div className="border-t border-slate-200 pt-6">
                    <h3 className="text-lg font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-sky-500" />
                      Preferred Dates
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Provide up to 3 preferred dates. We'll work with you to find the best option.
                    </p>
                    
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label>First Choice *</Label>
                        <Input
                          type="date"
                          required
                          min={getMinDate()}
                          value={formData.preferred_date_1}
                          onChange={(e) => setFormData({ ...formData, preferred_date_1: e.target.value })}
                          data-testid="request-date-1"
                        />
                      </div>
                      <div>
                        <Label>Second Choice</Label>
                        <Input
                          type="date"
                          min={getMinDate()}
                          value={formData.preferred_date_2}
                          onChange={(e) => setFormData({ ...formData, preferred_date_2: e.target.value })}
                          data-testid="request-date-2"
                        />
                      </div>
                      <div>
                        <Label>Third Choice</Label>
                        <Input
                          type="date"
                          min={getMinDate()}
                          value={formData.preferred_date_3}
                          onChange={(e) => setFormData({ ...formData, preferred_date_3: e.target.value })}
                          data-testid="request-date-3"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Additional Notes */}
                  <div className="border-t border-slate-200 pt-6">
                    <Label>Additional Notes</Label>
                    <Textarea
                      rows={4}
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Any special requirements, topics of interest, or questions..."
                      data-testid="request-notes"
                    />
                  </div>
                  
                  {/* Submit */}
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="w-full bg-sky-500 hover:bg-sky-600 text-white h-12"
                    data-testid="request-submit"
                  >
                    {submitting ? (
                      <>Submitting...</>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Course Request
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
          
          {/* Info Cards */}
          {!submitted && (
            <div className="grid md:grid-cols-3 gap-4 mt-8">
              <Card className="border-slate-200">
                <CardContent className="py-4 text-center">
                  <div className="text-2xl font-bold text-[#1E3A5F]">1-2 Days</div>
                  <div className="text-sm text-slate-500">Response Time</div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="py-4 text-center">
                  <div className="text-2xl font-bold text-[#1E3A5F]">Flexible</div>
                  <div className="text-sm text-slate-500">Scheduling</div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="py-4 text-center">
                  <div className="text-2xl font-bold text-[#1E3A5F]">TREC</div>
                  <div className="text-sm text-slate-500">Credit Reporting</div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#1E3A5F] mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-[#1E3A5F] mb-2">
                How far in advance should I book?
              </h3>
              <p className="text-slate-600">
                We recommend booking at least 2-3 weeks in advance to ensure availability. 
                However, we can often accommodate shorter notice requests depending on 
                instructor availability.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-[#1E3A5F] mb-2">
                What's the minimum group size?
              </h3>
              <p className="text-slate-600">
                For private sessions, we typically require a minimum of 5 participants. 
                Smaller groups may be accommodated on a case-by-case basis, or you can 
                join one of our public scheduled classes.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-[#1E3A5F] mb-2">
                How does TREC CE credit reporting work?
              </h3>
              <p className="text-slate-600">
                We collect each participant's TREC license number during registration. 
                After the course is completed, we submit CE credits directly to TREC 
                on behalf of all attendees within 10 business days.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-[#1E3A5F] mb-2">
                Can you come to our office?
              </h3>
              <p className="text-slate-600">
                Yes! For in-person sessions, we can come to your brokerage or team's 
                location. We just need a suitable meeting space with presentation 
                capabilities. Travel fees may apply for locations outside the greater 
                Houston area.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
